import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Loader2, Target } from 'lucide-react'
import { CreateDevelopmentAreaRequest, DevelopmentAreaType, IDPItemStatus, IDPDevelopmentArea } from '@/services/api/idpService'
import { KPASelector, KPAOption } from './KPASelector'

interface AddDevelopmentAreaDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: CreateDevelopmentAreaRequest) => Promise<void>
  idpId: string
  userId: string | number
  year: number
  editingDevelopmentArea?: IDPDevelopmentArea | null
  kpas?: KPAOption[]  // KPAs from user's objectives
}

export function AddDevelopmentAreaDialog({
  open,
  onOpenChange,
  onSubmit,
  idpId,
  userId,
  year,
  editingDevelopmentArea = null,
  kpas
}: AddDevelopmentAreaDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState<CreateDevelopmentAreaRequest>({
    title: '',
    description: '',
    area_type: DevelopmentAreaType.SKILLS,
    linked_kpa_ids: [],
    current_state: '',
    desired_state: '',
    employee_notes: '',
    priority: 3,
    item_status: IDPItemStatus.EMPLOYEE_PROPOSED,
  })

  // Pre-fill form when editing
  useEffect(() => {
    if (editingDevelopmentArea) {
      if (import.meta.env.DEV) console.log('🔍 Editing development area:', editingDevelopmentArea.title)
      console.log('📎 Linked KPI IDs:', editingDevelopmentArea.linked_kpa_ids)
      setFormData({
        title: editingDevelopmentArea.title,
        description: editingDevelopmentArea.description || '',
        area_type: editingDevelopmentArea.area_type,
        linked_kpa_ids: editingDevelopmentArea.linked_kpa_ids || [],
        current_state: editingDevelopmentArea.current_state || '',
        desired_state: editingDevelopmentArea.desired_state || '',
        employee_notes: editingDevelopmentArea.employee_notes || '',
        priority: editingDevelopmentArea.priority,
        item_status: editingDevelopmentArea.item_status,
      })
    } else {
      setFormData({
        title: '',
        description: '',
        area_type: DevelopmentAreaType.SKILLS,
        linked_kpa_ids: [],
        current_state: '',
        desired_state: '',
        employee_notes: '',
        priority: 3,
        item_status: IDPItemStatus.EMPLOYEE_PROPOSED,
      })
    }
  }, [editingDevelopmentArea, open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.title.trim() || !formData.description?.trim()) return

    setIsSubmitting(true)
    try {
      if (import.meta.env.DEV) console.log('💾 Submitting development area:', formData.title)
      console.log('📎 Linked KPI IDs being saved:', formData.linked_kpa_ids)
      await onSubmit(formData)
      onOpenChange(false)
    } catch (error) {
      console.error('Failed to save development area:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleAddKPI = (kpiId: string) => {
    setFormData({
      ...formData,
      linked_kpa_ids: [...(formData.linked_kpa_ids || []), kpiId],
    })
  }

  const handleRemoveKPI = (kpiId: string) => {
    setFormData({
      ...formData,
      linked_kpa_ids: (formData.linked_kpa_ids || []).filter((id: string) => id !== kpiId),
    })
  }


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-amber-500" />
            {editingDevelopmentArea ? 'Edit Development Area' : 'Add Development Area'}
          </DialogTitle>
          <DialogDescription>
            Identify an area for growth and improvement in your performance.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">
              Development Area Title <span className="text-red-500">*</span>
            </Label>
            <Input
              id="title"
              value={formData.title}
              onChange={e => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Time Management Under Pressure"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="area_type">
              Development Type <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.area_type}
              onValueChange={(value: DevelopmentAreaType) =>
                setFormData({ ...formData, area_type: value })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={DevelopmentAreaType.KNOWLEDGE}>Knowledge Gap</SelectItem>
                <SelectItem value={DevelopmentAreaType.SKILLS}>Skills Application</SelectItem>
                <SelectItem value={DevelopmentAreaType.VALUES_ATTITUDE}>Values & Attitude</SelectItem>
                <SelectItem value={DevelopmentAreaType.PERSONAL_CAPACITY}>Personal Capacity</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">
              Description <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what needs development and why..."
              rows={4}
              required
            />
          </div>

          {/* KPA Selector - Only render when dialog is open and KPAs are available */}
          {open && kpas && kpas.length > 0 && (
            <KPASelector
              selectedKPAIds={formData.linked_kpa_ids || []}
              onAdd={handleAddKPI}
              onRemove={handleRemoveKPI}
              label="Linked KPAs"
              kpas={kpas}
            />
          )}


          <div className="space-y-2">
            <Label htmlFor="notes">Personal Notes</Label>
            <Textarea
              id="notes"
              value={formData.employee_notes}
              onChange={e => setFormData({ ...formData, employee_notes: e.target.value })}
              placeholder="Add any personal notes or context..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !formData.title.trim() || !formData.description?.trim()}
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingDevelopmentArea ? 'Save Changes' : 'Add Development Area'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
